// zod schemas for auth flows (login + register + password reset + email tokens).
//
// Per W2 §3.3.2 #2 + §3.2 #27: zod schemas are the canonical source-of-truth
// shared by FE (instant validation) and BE (Pydantic-side mirror).
//
// The BE enforces the same bounds in Pydantic, so this file is duplicated
// as `app/modules/auth/schemas.py::RegisterRequest` etc.  When the contract
// drifts, ``pnpm gen:api`` will surface the mismatch via the dev check-schema
// script (per ADR-0003).

import { z } from "zod";

export const identifierSchema = z.string().min(1).max(254);
export const passwordSchema = z.string().min(8, "至少 8 個字元").max(128);
export const displayNameSchema = z
  .string()
  .min(1, "請輸入顯示名稱")
  .max(50, "最長 50 字")
  .transform((s) => s.trim())
  .refine((s) => s.length > 0, "顯示名稱不可空白");

export const emailSchema = z
  .string()
  .email("Email 格式不正確")
  .max(254)
  .optional()
  .or(z.literal(""));

export const usernameSchema = z
  .string()
  .min(3, "帳號至少 3 字")
  .max(20, "帳號最長 20 字")
  .regex(/^[a-zA-Z_][a-zA-Z0-9_]{2,19}$/, "帳號僅能英文數字底線")
  .optional()
  .or(z.literal(""));

export const inviteCodeSchema = z.string().regex(/^[1-9][0-9]{5}$/, "邀請碼為 6 位數字");

export const emailTokenSchema = z.string().min(10, "連結無效").max(128, "連結無效");

// --- Login ----------------------------------------------------------------

export const loginSchema = z.object({
  identifier: identifierSchema,
  password: z.string().min(1, "請輸入密碼"),
  remember: z.boolean().optional(),
});

export type LoginInput = z.infer<typeof loginSchema>;

// --- Register -------------------------------------------------------------

export const registerSchema = z
  .object({
    email: emailSchema,
    username: usernameSchema,
    password: passwordSchema,
    confirm: z.string(),
    display_name: displayNameSchema,
    invited_by_code: inviteCodeSchema.optional().or(z.literal("")),
  })
  .superRefine((value, ctx) => {
    if (!value.email && !value.username) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["email"],
        message: "請填寫電子郵件或使用者名稱",
      });
    }
    if (value.password !== value.confirm) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["confirm"],
        message: "兩次密碼不一致",
      });
    }
  });

export type RegisterInput = z.infer<typeof registerSchema>;

// --- Password reset / email verification ---------------------------------

export const forgotPasswordSchema = z.object({
  email: z.string().email("Email 格式不正確").max(254),
});
export const resetPasswordSchema = z
  .object({
    token: emailTokenSchema,
    password: passwordSchema,
    confirm: z.string(),
  })
  .superRefine((value, ctx) => {
    if (value.password !== value.confirm) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["confirm"],
        message: "兩次密碼不一致",
      });
    }
  });

export const verifyEmailSchema = z.object({
  token: emailTokenSchema,
});

export const changePasswordSchema = z
  .object({
    old_password: z.string().min(1),
    new_password: passwordSchema,
    confirm: z.string(),
  })
  .superRefine((value, ctx) => {
    if (value.new_password !== value.confirm) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["confirm"],
        message: "兩次密碼不一致",
      });
    }
  });
